import {
  Badge,
  Button,
  Col,
  Dropdown,
  FormGroup,
  FormSelect,
  Row,
  Table,
} from "react-bootstrap";
import { TableContainer } from "../TableContainer";
import ApiTableHeader from "./ApiTableHeader";
import { ApiTableProps } from "../../types/ApiTable/ApiTableProps";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import ApiTableFooter from "./ApiTableFooter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faRefresh,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import ApiTableFilter from "./ApiTableFilter";
import { TableContext } from "./Context/TableContext";
import { ApiTableColumn } from "../../types/ApiTable/ApiTableColumn";
import { useMemo, useState } from "react";
import { ApiTableSorting } from "../../types/ApiTable/ApiTableSorting";
import { ApiTablePagination } from "../../types/ApiTable/ApiTablePagination";
import { ApiTableFiltering } from "../../types/ApiTable/ApiTableFiltering";
import IconButton from "../IconButton";

const ApiTable = ({
  state,
  data,
  columns,
  pageOptions,
  onChange,
  allowRefresh,
  loading,
  controls,
}: ApiTableProps) => {
  const filterColumns = useMemo(() => {
    return columns
      .filter((column) => !column.hide)
      .filter((column) => column.filter);
  }, [columns]);

  const [selectedFilterColumn, setSelectedFilterColumn] = useState<
    ApiTableColumn | undefined
  >(columns.filter((column) => !column.hide).find((x) => x.filter));

  const handleFilterColumnChange = (key: string | null) => {
    if (key == null) return;
    const index = parseInt(key);
    setSelectedFilterColumn(filterColumns[index]);
  };

  const updateSort = (sorting: ApiTableSorting) => {
    onChange({ ...state, sort: sorting });
  };

  const updateFilter = (filter: ApiTableFiltering) => {
    const filters = state.filters ?? [];

    // Append or update filter
    const existingFilter = filters.find((f) => f.field === filter.field);
    if (existingFilter) {
      existingFilter.filterServiceText = filter.filterServiceText;
      existingFilter.filterText = filter.filterText;
      existingFilter.value = filter.value;
    } else {
      filters.push(filter);
    }

    const filterExpression = filters
      .filter((f) => !!f.filterServiceText)
      .map((f) => f.filterServiceText)
      .join(" _AND ");

    onChange({
      ...state,
      filters: filters,
      filterExpression: filterExpression,
      pagination: {
        page: 1,
        pageSize: state.pagination.pageSize,
      },
    });
  };

  const updatePagination = (pagination: ApiTablePagination) => {
    onChange({
      ...state,
      pagination: pagination,
    });
  };

  const clearFilter = (field: string) => {
    let filters = state.filters ?? [];
    filters = filters.filter((f) => f.field !== field);

    const filterExpression = filters
      .filter((f) => !!f.filterServiceText)
      .map((f) => f.filterServiceText)
      .join(" _AND ");

    onChange({
      ...state,
      filters: filters,
      filterExpression: filterExpression,
      pagination: {
        page: 1,
        pageSize: state.pagination.pageSize,
      },
    });
  };

  const refresh = () => {
    onChange({ ...state });
  };

  return (
    <TableContext.Provider
      value={{
        state,
        updateSort,
        updateFilter,
        updatePagination,
        clearFilter,
        columns,
      }}
    >
      <Row>
        {selectedFilterColumn && (
          <Col md="6">
            <div className="h-100 mr-2 d-flex flex-row justify-content-start align-items-center">
              {filterColumns.length > 0 &&
                filterColumns.map((column, index) =>
                  selectedFilterColumn.field === column.field ? (
                    <ApiTableFilter column={column} key={index} />
                  ) : null
                )}
              {filterColumns.length > 1 && (
                <Dropdown onSelect={handleFilterColumnChange} className="mx-1">
                  <Dropdown.Toggle id="dropdown-basic">
                    <FontAwesomeIcon icon={faFilter} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {filterColumns.map((column, index) => (
                      <Dropdown.Item eventKey={index} key={index}>
                        {column.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </Col>
        )}

        {controls && <Col>{controls}</Col>}
        <Col>
          <div className="mb-2 d-flex flex-row justify-content-end align-items-center h-100">
            <label htmlFor="records">Records per page</label>
            <FormSelect
              id="records"
              className="ms-2"
              style={{ width: "75px" }}
              value={state.pagination.pageSize}
              onChange={(e: React.ChangeEvent<any>) =>
                updatePagination({
                  page: 1,
                  pageSize: parseInt(e.target.value),
                })
              }
            >
              {pageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </FormSelect>
            {allowRefresh && (
              <IconButton icon={faRefresh} onClick={refresh} className="ms-2" />
            )}
          </div>
        </Col>
      </Row>
      <Row className="my-2">
        <Col>
          {state.filters &&
            state.filters.map((f, index) => (
              <Badge pill bg="primary" className="me-1" key={index}>
                {f.filterText}{" "}
                <FontAwesomeIcon
                  icon={faXmark}
                  onClick={() => clearFilter(f.field!)}
                />
              </Badge>
            ))}
        </Col>
      </Row>
      <TableContainer className="mb-1">
        <Table bordered hover striped>
          <thead>
            <tr>
              {columns
                .filter((x) => !x.hide)
                .map((column, index) => (
                  <ApiTableHeader column={column} key={index} />
                ))}
            </tr>
          </thead>
          <tbody>
            {!loading &&
              data.records.map((row, index) => (
                <tr key={index}>
                  {columns
                    .filter((x) => !x.hide)
                    .map((column, index) => (
                      <td key={index}>{column.renderContents(row)}</td>
                    ))}
                </tr>
              ))}
            {loading && (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  <LoadingSpinner />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>
      <ApiTableFooter
        currentPage={data.page}
        setCurrentPage={(page) =>
          onChange({
            ...state,
            pagination: {
              ...state.pagination,
              page: page,
            },
          })
        }
        totalPages={data.totalPages}
      />
    </TableContext.Provider>
  );
};

export default ApiTable;
